import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DateRangeModal from "../leads/DateRangeModal";
import { DocumentIcon } from "../../lib/icons";
import InventoryStatCards from "./InventoryStatCards";
import SearchableSelect from "../common/SearchableSelect";
import { exportVehicleInventory } from "../../features/inventory/api/vehicleInventory.api";
import {
  useDealerInventory,
  useInventoryFilterOptions,
  useVehicleInventoryBreakdowns,
  useVehicleInventorySummary,
} from "../../features/inventory/hooks/useVehicleInventory";

const numberFormat = new Intl.NumberFormat("en-US");

const CHART_COLORS = [
  "#5c85ff",
  "#9b79ff",
  "#ff9d4d",
  "#41b978",
  "#ff6f6f",
  "#69c799",
  "#c8d4f0",
];

function DonutChart({ segments, total }) {
  const r = 56;
  const cx = 70;
  const cy = 70;
  const circumference = 2 * Math.PI * r;

  const arcs = useMemo(() => {
    if (!total) return [];
    let offset = 0;
    return segments.map((seg, i) => {
      const frac = Math.max(seg.units, 0) / total; // guard against negative counts from the API
      const dash = frac * circumference;
      const gap = circumference - dash;
      const rotation = offset * 360 - 90;
      offset += frac;
      return { dash, gap, rotation, color: CHART_COLORS[i % CHART_COLORS.length] };
    });
  }, [segments, total, circumference]);

  return (
    <svg width="120" height="120" viewBox="0 0 140 140" className="inv-donut-svg">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e6ebf5" strokeWidth="18" />
      {arcs.map((arc, i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={arc.color}
          strokeWidth="18"
          strokeDasharray={`${arc.dash} ${arc.gap}`}
          strokeDashoffset="0"
          style={{ transform: `rotate(${arc.rotation}deg)`, transformOrigin: `${cx}px ${cy}px` }}
        />
      ))}
      <text x={cx} y={cy - 4} textAnchor="middle" className="inv-donut-total">{numberFormat.format(total || 0)}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" className="inv-donut-label">Total Units</text>
    </svg>
  );
}

function BreakdownCard({ title, segments, total, onFilter, tableRef, isActive }) {
  const colLabel = title.replace("By ", "");

  function handleRowClick(seg) {
      if (seg.isOthers) return;
    onFilter(seg);
    if (tableRef?.current) {
      tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  return (
    <div className="inv-breakdown-card">
      <div className="inv-breakdown-header">
        <span className="inv-breakdown-title">{title}</span>
      </div>
      <div className="inv-breakdown-body">
        <DonutChart segments={segments} total={total} />
        <table className="inv-breakdown-table">
          <thead>
            <tr>
              <th>{colLabel}</th>
              <th>Units</th>
            </tr>
          </thead>
          <tbody>
            {segments.length === 0 ? (
              <tr><td colSpan={2} style={{ color: "var(--muted)", fontSize: 12, paddingTop: 12 }}>No data</td></tr>
            ) : segments.map((seg, i) => (
              <tr
                key={seg.id ?? seg.slug ?? `${seg.name}-${i}`}
                className={[
                  !seg.isOthers ? "inv-breakdown-row-clickable" : "",
                  isActive?.(seg) ? "inv-breakdown-row-active" : "",
                ].join(" ").trim()}
                onClick={() => handleRowClick(seg)}
                title={!seg.isOthers ? `Filter table by ${seg.name}` : undefined}
              >
                <td>
                  <span className="inv-dot" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="inv-breakdown-name" title={seg.name}>{seg.name}</span>
                </td>
                <td>{numberFormat.format(seg.units)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const PAGE_SIZE = 12;

const TABLE_COLUMNS = [
  { key: "oem",         label: "OEM" },
  { key: "dealer",      label: "Dealer" },
  { key: "total",       label: "Purchase",               right: true },
  { key: "onboarded",   label: "Sales (Assigned)",       right: true },
  { key: "unonboarded", label: "Inventory (Unassigned)", right: true, highlight: true },
];

/** Page list with ellipses: 1 … 4 5 6 … 20 */
function pageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (current >= total - 2) [total - 1, total - 2, total - 3].forEach((p) => pages.add(p));
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i && p - sorted[i - 1] > 1) out.push(`gap-${p}`);
    out.push(p);
  });
  return out;
}

function formatTime(date) {
  if (!date) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function InventoryView() {
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    if (!exportMenuOpen) return;
    function handleClick(e) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [exportMenuOpen]);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Keep slug arrays in state; serialize once for all inventory APIs.
  const [filterOem, setFilterOem] = useState([]);
  const [filterDealer, setFilterDealer] = useState([]);
  const [filterCity, setFilterCity] = useState([]);
  const [sortCol, setSortCol] = useState("oem");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);

  const hasFilters = Boolean(filterOem.length || filterDealer.length || filterCity.length);

  /* ─── Dropdown options ───
     OEM: /api/vehicles/oem/ · Dealer: /api/vehicles/dealer/ · City: /api/geographies/geography/cities/
     Each option is { value: slug, label: name, slug }. */
  const options = useInventoryFilterOptions();
  const oemOptions = useMemo(() => options.data?.oems || [], [options.data]);
  const dealerOptions = useMemo(() => options.data?.dealers || [], [options.data]);
  const cityOptions = useMemo(() => options.data?.cities || [], [options.data]);

  const filters = useMemo(
    () => ({
      oem: filterOem.join(","),
      dealer: filterDealer.join(","),
      city: filterCity.join(","),
      // Date range applies to every vehicle-inventory API (summary, breakdown, table).
      fromDate: dateFrom,
      toDate: dateTo,
    }),
    [filterOem, filterDealer, filterCity, dateFrom, dateTo],
  );

  /* ─── Export (download-initiate → download?task_id) ─── */
  const [exporting, setExporting] = useState(null); // null | "excel" | "csv"
  const [exportError, setExportError] = useState("");
  const exportAbortRef = useRef(null);
  useEffect(() => () => exportAbortRef.current?.abort(), []);

  async function handleExport(fileType) {
    setExportMenuOpen(false);
    if (exporting) return;
    const controller = new AbortController();
    exportAbortRef.current = controller;
    setExporting(fileType);
    setExportError("");
    try {
      await exportVehicleInventory({ fileType, signal: controller.signal, ...filters });
    } catch (err) {
      if (err?.name !== "AbortError") setExportError(err?.message || "Export failed.");
    } finally {
      if (!controller.signal.aborted) setExporting(null);
    }
  }

  /* ─── API data ─── */
  const summary = useVehicleInventorySummary(filters);
  const breakdowns = useVehicleInventoryBreakdowns(filters, { limit: 6 });
  const table = useDealerInventory({
    ...filters,
    sort: `${sortDir === "desc" ? "-" : ""}${sortCol}`,
    page,
    pageSize: PAGE_SIZE,
  });

  // Left card + "last synced" come straight from the breakdown API.
  const totalUnit = breakdowns.data?.totalUnit ?? null;
  const lastSynced = breakdowns.data?.lastSyncedDate ?? null;

  const refreshing = summary.loading || breakdowns.loading || table.loading;
  const refreshAll = useCallback(() => {
    summary.refetch();
    breakdowns.refetch();
    table.refetch();
  }, [summary.refetch, breakdowns.refetch, table.refetch]);

  /* ─── Table helpers ─── */
  function toggleSort(col) {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      // Numbers read best largest-first; text A→Z.
      setSortDir(["total", "onboarded", "unonboarded"].includes(col) ? "desc" : "asc");
    }
    setPage(1);
  }

  const setFilter = (setter) => (val) => {
    setter(val);
    setPage(1);
  };

  function clearFilters() {
    setFilterOem([]);
    setFilterDealer([]);
    setFilterCity([]);
    setPage(1);
  }

  function filterFromChart(setter) {
    return (seg) => {
      if (!seg.slug) return;
      setter([seg.slug]);
      setPage(1);
      if (tableRef.current) tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    };
  }

  const count = table.data?.count ?? 0;
  const totalPages = Math.max(Math.ceil(count / PAGE_SIZE), 1);
  const rows = table.data?.results || [];

  // If filters shrink the result set below the current page, step back.
  useEffect(() => {
    if (table.data && page > totalPages) setPage(totalPages);
  }, [table.data, page, totalPages]);

  function formatShortDate(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  }

  const dateLabel = dateFrom || dateTo
    ? `${formatShortDate(dateFrom) || "…"} – ${formatShortDate(dateTo) || "…"}`
    : "Filter by Date";
  const dateFilterActive = Boolean(dateFrom || dateTo);

  const fmt = (n) => numberFormat.format(n || 0);

  return (
    <>
    <div className="inv-root">
      {/* Page header */}
      <div className="inv-page-header">
        <div>
          <h1 className="inv-page-title">Inventory</h1>
          <p className="inv-page-subtitle">Track your inventory availability across OEMs, Cities and Dealerships.</p>
        </div>
        <div className="inv-header-actions">
          <button
            type="button"
            className={`inv-btn-outline${dateFrom || dateTo ? " inv-btn-outline-active" : ""}`}
            onClick={() => setDateModalOpen(true)}
          >
            <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="4.5" width="14" height="12" rx="2" />
              <path d="M6.5 3v3M13.5 3v3M3 8.5h14" strokeLinecap="round" />
            </svg>
            {dateLabel}
            {(dateFrom || dateTo) && (
              <span
                className="inv-date-clear"
                role="button"
                tabIndex={0}
                title="Clear date filter"
                onClick={(e) => { e.stopPropagation(); setDateFrom(""); setDateTo(""); setPage(1); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setDateFrom(""); setDateTo(""); setPage(1); } }}
              >✕</span>
            )}
          </button>
          <div className="inv-export-menu" ref={exportMenuRef}>
            <button
              type="button"
              className="inv-btn-primary"
              onClick={() => setExportMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={exportMenuOpen}
              disabled={Boolean(exporting)}
              aria-busy={Boolean(exporting)}
            >
              {exporting ? (
                <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" className="inv-spin">
                  <path d="M16.5 10A6.5 6.5 0 1 1 10 3.5" strokeLinecap="round" />
                </svg>
              ) : (
                <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M10 3.5v9m0 0-3-3m3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M4 14.5h12" strokeLinecap="round" />
                </svg>
              )}
              {exporting ? "Preparing…" : "Export"}
              {!exporting && <span className="inv-export-caret" aria-hidden="true">▾</span>}
            </button>
            {exportMenuOpen && (
              <div className="inv-export-dropdown" role="menu">
                <button type="button" role="menuitem" className="inv-export-option" onClick={() => handleExport("excel")}>
                  <DocumentIcon />
                  <span>Download Excel</span>
                </button>
                <button type="button" role="menuitem" className="inv-export-option" onClick={() => handleExport("csv")}>
                  <DocumentIcon />
                  <span>Download CSV</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {exportError && (
        <div className="inv-stat-error">
          <span>Export failed: {exportError}</span>
          <button type="button" onClick={() => setExportError("")}>Dismiss</button>
        </div>
      )}

      {/* Vehicle summary stat cards */}
      <InventoryStatCards
        data={summary.data}
        loading={summary.loading}
        error={summary.error}
        onRetry={summary.refetch}
      />

      {/* Top summary row */}
      <div className="inv-top-row">
        <div className="inv-total-card">
          <p className="inv-total-label">
            INVENTORY AVAILABLE
            <span className="inv-info-dot" title="Total units across all OEMs, cities and dealerships">ⓘ</span>
          </p>
          <p className="inv-total-number">
            {totalUnit !== null ? fmt(totalUnit) : "—"}
          </p>
          <p className="inv-total-sub">Total Units</p>
          {lastSynced && (
            <p className="inv-sync-time">
              <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.4">
                <circle cx="8" cy="8" r="5.5" />
                <path d="M8 5v3.2l2 1.2" strokeLinecap="round" />
              </svg>
              Last synced: {formatTime(lastSynced)}
            </p>
          )}
          <button type="button" className="inv-sync-btn" onClick={refreshAll} disabled={refreshing}>
            <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" className={refreshing ? "inv-spin" : ""}>
              <path d="M16.5 10A6.5 6.5 0 0 1 5.2 14.3M3.5 10A6.5 6.5 0 0 1 14.8 5.7" strokeLinecap="round" />
              <path d="M14 4.5 15.5 5.7 14 7M6 13 4.5 14.3 6 15.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {breakdowns.loading && !breakdowns.data ? (
          <>
            <div className="inv-breakdown-card inv-skeleton" />
            <div className="inv-breakdown-card inv-skeleton" />
            <div className="inv-breakdown-card inv-skeleton" />
          </>
        ) : (
          <>
            <BreakdownCard
              title="By City"
              segments={breakdowns.data?.city.results || []}
              total={breakdowns.data?.totalUnit || breakdowns.data?.city.totalUnits || 0}
              tableRef={tableRef}
              isActive={(seg) => filterCity.includes(seg.slug)}
              onFilter={filterFromChart(setFilterCity)}
            />
            <BreakdownCard
              title="By OEM"
              segments={breakdowns.data?.oem.results || []}
              total={breakdowns.data?.totalUnit || breakdowns.data?.oem.totalUnits || 0}
              tableRef={tableRef}
              isActive={(seg) => filterOem.includes(seg.slug)}
              onFilter={filterFromChart(setFilterOem)}
            />
            <BreakdownCard
              title="By Dealership"
              segments={breakdowns.data?.dealer.results || []}
              total={breakdowns.data?.totalUnit || breakdowns.data?.dealer.totalUnits || 0}
              tableRef={tableRef}
              isActive={(seg) => filterDealer.includes(seg.slug)}
              onFilter={filterFromChart(setFilterDealer)}
            />
          </>
        )}
      </div>

      {breakdowns.error && (
        <div className="inv-stat-error">
          <span>Couldn't load inventory breakdown: {breakdowns.error}</span>
          <button type="button" onClick={breakdowns.refetch}>Retry</button>
        </div>
      )}

      {/* Date filter active notice */}
      {dateFilterActive && (
        <div className="inv-date-notice">
          <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="4.5" width="14" height="12" rx="2" />
            <path d="M6.5 3v3M13.5 3v3M3 8.5h14" strokeLinecap="round" />
          </svg>
          Showing data for: <strong>{formatShortDate(dateFrom) || "…"} – {formatShortDate(dateTo) || "…"}</strong>
          <button type="button" className="inv-date-notice-clear" onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}>
            Clear
          </button>
        </div>
      )}

      {/* B2C Dealer Dashboard */}
      <div className="inv-table-section" ref={tableRef}>
        <div className="inv-table-header">
          <div>
            <h2 className="inv-table-title">B2C Dealer Dashboard</h2>
            {lastSynced && (
              <p className="inv-table-refresh">
                Last synced: {formatTime(lastSynced)}
              </p>
            )}
          </div>
          <div className="inv-table-filters">
            <SearchableSelect
              className="inv-filter-dropdown"
              ariaLabel="OEMs"
              placeholder="All OEMs"
              multiple
              value={filterOem}
              onChange={setFilter(setFilterOem)}
              options={oemOptions}
              loadOptions={options.loaders?.oems}
              disabled={options.loading && !options.data}
            />
            <SearchableSelect
              className="inv-filter-dropdown"
              ariaLabel="Dealers"
              placeholder="All Dealers"
              multiple
              value={filterDealer}
              onChange={setFilter(setFilterDealer)}
              options={dealerOptions}
              loadOptions={options.loaders?.dealers}
              disabled={options.loading && !options.data}
            />
            <SearchableSelect
              className="inv-filter-dropdown"
              ariaLabel="Cities"
              placeholder="All Cities"
              multiple
              value={filterCity}
              onChange={setFilter(setFilterCity)}
              options={cityOptions}
              loadOptions={options.loaders?.cities}
              disabled={options.loading && !options.data}
            />
            {hasFilters && (
              <button
                type="button"
                className="inv-btn-outline"
                style={{ padding: "4px 10px", fontSize: 12 }}
                onClick={clearFilters}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {options.error && (
          <div className="inv-stat-error" style={{ margin: "0 20px 12px" }}>
            <span>Couldn't load filter options: {options.error}</span>
            <button type="button" onClick={options.refetch}>Retry</button>
          </div>
        )}

        <table className={`inv-main-table${table.loading && table.data ? " inv-table-reloading" : ""}`}>
          <thead>
            <tr>
              {TABLE_COLUMNS.map(({ key, label, right, highlight }) => (
                <th
                  key={key}
                  className={[
                    "inv-th-sort",
                    right ? "inv-col-right" : "",
                    highlight ? "inv-col-highlight" : "",
                  ].join(" ").trim()}
                  onClick={() => toggleSort(key)}
                  aria-sort={sortCol === key ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                >
                  {label}
                  <span className="inv-sort-icon">
                    {sortCol === key ? (sortDir === "asc" ? " ↑" : " ↓") : " ↕"}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.loading && !table.data ? (
              <tr><td colSpan={TABLE_COLUMNS.length} className="inv-table-empty">Loading…</td></tr>
            ) : table.error ? (
              <tr><td colSpan={TABLE_COLUMNS.length} className="inv-table-empty">
                {table.error}{" "}
                <button type="button" className="inv-btn-outline" style={{ padding: "3px 10px", fontSize: 12, marginLeft: 8 }} onClick={table.refetch}>
                  Retry
                </button>
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={TABLE_COLUMNS.length} className="inv-table-empty">
                {hasFilters ? "No rows match the current filters." : "No data available."}
              </td></tr>
            ) : rows.map((row) => (
              <tr key={`${row.oem.id}-${row.dealer.id}-${row.city.id}`}>
                <td>{row.oem.name}</td>
                <td>{row.dealer.name}</td>
                <td className="inv-col-right">{fmt(row.total)}</td>
                <td className="inv-col-right">{fmt(row.onboarded)}</td>
                <td className="inv-col-right inv-inv-val">{fmt(row.unonboarded)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="inv-table-footer">
          <span className="inv-count-label">
            {table.data && `Showing ${count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} to ${Math.min(page * PAGE_SIZE, count)} of ${fmt(count)} rows`}
          </span>
          <div className="inv-pagination">
            <button type="button" className="inv-page-btn" disabled={page <= 1 || table.loading} onClick={() => setPage((p) => p - 1)}>‹</button>
            {pageList(page, totalPages).map((p) =>
              typeof p === "string" ? (
                <span key={p} className="inv-page-gap">…</span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={`inv-page-btn ${p === page ? "active" : ""}`}
                  onClick={() => setPage(p)}
                  disabled={table.loading && p !== page}
                >{p}</button>
              ),
            )}
            <button type="button" className="inv-page-btn" disabled={page >= totalPages || table.loading} onClick={() => setPage((p) => p + 1)}>›</button>
          </div>
        </div>
      </div>
    </div>

    <DateRangeModal
      open={dateModalOpen}
      startValue={dateFrom}
      endValue={dateTo}
      onApply={(start, end) => {
        setDateFrom(start);
        setDateTo(end);
        setPage(1);
        setDateModalOpen(false);
      }}
      onClose={() => setDateModalOpen(false)}
    />
    </>
  );
}

export default InventoryView;
